// Hand — palmar (palm-facing) detail panel for pain capture
// (PAIN_TRACKING_PLAN.md Task 3.1). React-free / JSX-free (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// Own canvas (D-PAIN-6, "Canvas conventions"): 0 0 200 260, centerline x = 100.
// ONE left hand is drawn; PainMapCanvas mirrors it to the right hand at render
// time via mirrorPath, so there is never a hand-authored right-hand copy. Every
// region is therefore a single "-l" region (side draws one limb only — the
// validator's paired rule S4 applies to front/back exclusively).
//
// Layout: fingers point up (fingertips near y = 20, MCP row near y = 146), palm
// y 140-232, wrist y 230-256. This is a LEFT hand seen palm-up, so the thumb is
// on the viewer's right (high x) and the little finger on the viewer's left.
//
// Coverage (Task 3.1, palmar aspect): MCP / PIP / DIP joint blocks per digit,
// wrist joint, scaphoid (thumb-side wrist hollow), median nerve (carpal tunnel
// zone), ulnar nerve (hypothenar border), and tendon / intrinsic soft-tissue
// bands (thenar eminence, hypothenar eminence, central flexor band).

import type { PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { ellipsePath } from "./painRegions.js";

export const HAND_PALMAR_VIEWBOX = "0 0 200 260";

/** A closed rounded-rectangle outline path (palm / wrist / thumb blocks). */
function box(x0: number, y0: number, x1: number, y1: number, r: number): string {
  return [
    `M ${x0 + r} ${y0}`,
    `L ${x1 - r} ${y0}`,
    `Q ${x1} ${y0} ${x1} ${y0 + r}`,
    `L ${x1} ${y1 - r}`,
    `Q ${x1} ${y1} ${x1 - r} ${y1}`,
    `L ${x0 + r} ${y1}`,
    `Q ${x0} ${y1} ${x0} ${y1 - r}`,
    `L ${x0} ${y0 + r}`,
    `Q ${x0} ${y0} ${x0 + r} ${y0}`,
    "Z",
  ].join(" ");
}

/** A rounded finger capsule from its tip (yTop) down to the MCP row (y = 150). */
function finger(cx: number, yTop: number): string {
  const yBase = 150;
  const w = 6;
  return [
    `M ${cx - w} ${yBase}`,
    `L ${cx - w} ${yTop + 6}`,
    `Q ${cx - w} ${yTop} ${cx} ${yTop}`,
    `Q ${cx + w} ${yTop} ${cx + w} ${yTop + 6}`,
    `L ${cx + w} ${yBase}`,
    "Z",
  ].join(" ");
}

export const HAND_PALMAR_OUTLINE: string[] = [
  box(66, 140, 132, 232, 14), // palm
  box(76, 230, 128, 256, 10), // wrist
  box(132, 152, 158, 214, 12), // thumb
  finger(122, 32), // index (d2)
  finger(106, 20), // middle (d3)
  finger(90, 26), // ring (d4)
  finger(74, 40), // little (d5)
];

/** One left-hand-only region. `kind` is read from PAIN_STRUCTURE_META so a
 *  region can never disagree with the taxonomy (validator rule S7). */
function reg(id: string, structure: PainStructure, d: string): PainRegion {
  return { id, structure, kind: PAIN_STRUCTURE_META[structure].kind, side: "left", d };
}

// Finger centrelines (viewer-left little finger -> thumb on the viewer's right).
const D2 = 122; // index
const D3 = 106; // middle
const D4 = 90; // ring
const D5 = 74; // little
const THUMB = 146;

export const HAND_PALMAR_STRUCTURES: PainRegion[] = [
  // ── MCP row (base of each digit) ──────────────────────────────────────────
  reg("joint-mcp-d1-l", "joint-mcp", ellipsePath(THUMB, 200, 5.5, 5)),
  reg("joint-mcp-d2-l", "joint-mcp", ellipsePath(D2, 146, 5, 4.5)),
  reg("joint-mcp-d3-l", "joint-mcp", ellipsePath(D3, 146, 5, 4.5)),
  reg("joint-mcp-d4-l", "joint-mcp", ellipsePath(D4, 146, 5, 4.5)),
  reg("joint-mcp-d5-l", "joint-mcp", ellipsePath(D5, 146, 5, 4.5)),

  // ── PIP row (middle joint; the thumb has no PIP) ──────────────────────────
  reg("joint-pip-d2-l", "joint-pip", ellipsePath(D2, 100, 5, 4.5)),
  reg("joint-pip-d3-l", "joint-pip", ellipsePath(D3, 100, 5, 4.5)),
  reg("joint-pip-d4-l", "joint-pip", ellipsePath(D4, 100, 5, 4.5)),
  reg("joint-pip-d5-l", "joint-pip", ellipsePath(D5, 100, 5, 4.5)),

  // ── DIP row (fingertip joint; d1 = the thumb IP joint) ────────────────────
  reg("joint-dip-d1-l", "joint-dip", ellipsePath(THUMB, 156, 5, 4.5)),
  reg("joint-dip-d2-l", "joint-dip", ellipsePath(D2, 56, 4.5, 4)),
  reg("joint-dip-d3-l", "joint-dip", ellipsePath(D3, 52, 4.5, 4)),
  reg("joint-dip-d4-l", "joint-dip", ellipsePath(D4, 56, 4.5, 4)),
  reg("joint-dip-d5-l", "joint-dip", ellipsePath(D5, 64, 4.5, 4)),

  // ── wrist ────────────────────────────────────────────────────────────────
  reg("joint-wrist-l", "joint-wrist", ellipsePath(102, 240, 20, 8)),
  reg("bone-scaphoid-l", "bone-scaphoid", ellipsePath(126, 228, 6, 6)),

  // ── nerves ───────────────────────────────────────────────────────────────
  // Median: carpal-tunnel zone up the centre of the palm.
  reg("nerve-median-l", "nerve-median", ellipsePath(100, 192, 6, 44)),
  // Ulnar: the hypothenar (little-finger) border.
  reg("nerve-ulnar-l", "nerve-ulnar", ellipsePath(72, 196, 5, 40)),

  // ── soft tissue (thenar / hypothenar intrinsics + central flexor band) ────
  reg("tissue-tendon-thenar-l", "tissue-tendon", ellipsePath(126, 196, 10, 20)),
  reg("tissue-tendon-hypothenar-l", "tissue-tendon", ellipsePath(74, 198, 8, 22)),
  reg("tissue-tendon-flexor-l", "tissue-tendon", ellipsePath(100, 170, 6, 26)),
];
